/**
 * Left rail (UI contract, as in kit-compliance): one flyout per semantic group with a muted tone.
 * "Spazi" comes first and lists the reader's Mosaico spazi, so a click lands in a wiki like
 * Outline's sidebar; the module groups come from the descriptors' `ui.navGroup`.
 */
import React from "react";
import {
  ApartmentOutlined, AppstoreOutlined, AuditOutlined, BookOutlined, CheckCircleOutlined, CodeOutlined,
  ExperimentOutlined, FileDoneOutlined, FileProtectOutlined, FileSearchOutlined, FileTextOutlined, FolderOutlined,
  GlobalOutlined, LockOutlined, PartitionOutlined, PlayCircleOutlined, ProjectOutlined, ReadOutlined, RobotOutlined, SafetyOutlined,
  SolutionOutlined, TeamOutlined, ThunderboltOutlined, UserOutlined,
} from "@ant-design/icons";
import { getIconComponent, registerIcons, type HubNavGroup } from "@evo-flywheel/ui";
import { descriptors } from "@kit/descriptors/index";
import type { SpazioInfo } from "./knowledge/mosaico/api";

registerIcons({
  AuditOutlined: <AuditOutlined />, BookOutlined: <BookOutlined />, CheckCircleOutlined: <CheckCircleOutlined />,
  CodeOutlined: <CodeOutlined />, ExperimentOutlined: <ExperimentOutlined />, FileDoneOutlined: <FileDoneOutlined />,
  FileProtectOutlined: <FileProtectOutlined />, FileSearchOutlined: <FileSearchOutlined />, FileTextOutlined: <FileTextOutlined />,
  PartitionOutlined: <PartitionOutlined />, PlayCircleOutlined: <PlayCircleOutlined />, ReadOutlined: <ReadOutlined />,
  SafetyOutlined: <SafetyOutlined />, ThunderboltOutlined: <ThunderboltOutlined />, UserOutlined: <UserOutlined />,
  FolderOutlined: <FolderOutlined />, ApartmentOutlined: <ApartmentOutlined />, AppstoreOutlined: <AppstoreOutlined />,
  ProjectOutlined: <ProjectOutlined />,
});

/** Descriptor groups, in rail order. Muted hues (AA on white). */
const GROUPS: Array<{ key: string; icon: React.ReactNode; tone: string }> = [
  { key: "Governance", icon: <SafetyOutlined />, tone: "#282A64" },
  { key: "Compliance", icon: <AuditOutlined />, tone: "#016BA5" },
  { key: "Policy e contenuti", icon: <FileProtectOutlined />, tone: "#0E7C72" },
  { key: "Formazione", icon: <SolutionOutlined />, tone: "#9A6B1F" },
  { key: "Ricerca", icon: <ExperimentOutlined />, tone: "#7A4E9C" },
  { key: "Agenti", icon: <RobotOutlined />, tone: "#4A5568" },
];
const KNOWLEDGE_TONE = "#B4532A";
const VIS_ICON: Record<SpazioInfo["visibilita"], React.ReactNode> = {
  privato: <UserOutlined />, riservato: <LockOutlined />, aziendale: <TeamOutlined />, pubblico: <GlobalOutlined />,
};

const uiOf = (slug: string) => {
  const d = descriptors.find((x) => x.slug === slug);
  return (typeof d?.ui === "object" && d.ui ? d.ui : {}) as { navGroup?: string; icon?: string };
};

export function buildNav(resources: Array<{ name: string; meta: { label: string } }>, spazi: SpazioInfo[]): HubNavGroup[] {
  const spaziGroup: HubNavGroup = {
    key: "spazi", label: "Spazi", icon: <BookOutlined />, tone: KNOWLEDGE_TONE,
    items: spazi.map((s) => ({ key: `spazio:${s.slug}`, label: `${s.titolo} · ${s.tessere}`, path: `/mosaico?spazio=${encodeURIComponent(s.slug)}`, icon: VIS_ICON[s.visibilita] })),
  };
  const knowledge: HubNavGroup = {
    key: "Conoscenza", label: "Conoscenza", icon: <AppstoreOutlined />, tone: KNOWLEDGE_TONE,
    items: [
      { key: "mosaico", label: "Il Mosaico", path: "/mosaico", icon: <AppstoreOutlined /> },
      { key: "documenti", label: "Archivio documenti", path: "/documenti", icon: <FolderOutlined /> },
      { key: "mappa", label: "Mappa del Mosaico", path: "/mappa", icon: <ApartmentOutlined /> },
      { key: "atomi", label: "Atomi", path: "/atomi", icon: <ExperimentOutlined /> },
      { key: "progetti", label: "Progetti", path: "/progetti", icon: <ProjectOutlined /> },
    ],
  };
  const modules = GROUPS.map((g) => ({
    key: g.key, label: g.key, icon: g.icon, tone: g.tone,
    items: resources.filter((r) => r.name !== "wiki_entry" && uiOf(r.name).navGroup === g.key)
      .map((r) => ({ key: r.name, label: r.meta.label, path: `/${r.name}`, icon: getIconComponent(uiOf(r.name).icon ?? "AppstoreOutlined") })),
  }));
  return [spaziGroup, knowledge, ...modules].filter((g) => g.items.length > 0);
}

/** MosaicoPage fires this after creating a spazio or saving a tessera, so the rail recounts. */
export const SPAZI_CHANGED = "mosaico:spazi-changed";
